import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { useGuestContext } from '../context/GuestContext';
import { getGuestTopic, getGuestTopicContent, type GuestTopicDetailDTO } from '../services/guestApi';
import GuestPracticeQuizCard from '../components/Quiz/GuestPracticeQuizCard';
import GuestAssessmentCard from '../components/Assessment/GuestAssessmentCard';

/**
 * Guest single-topic view – same content as student, plus quiz and assessment (guest limits apply).
 */
const GuestTopicView: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const { guestId, ensureSession, isReady, error: sessionError } = useGuestContext();
  const [topic, setTopic] = useState<GuestTopicDetailDTO | null>(null);
  const [contentFallback, setContentFallback] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ensureSession().catch(() => {});
  }, [ensureSession]);

  useEffect(() => {
    if (!isReady || !guestId || !topicId) {
      if (isReady && !topicId) setLoading(false);
      return;
    }
    const id = Number(topicId);
    if (Number.isNaN(id)) {
      setError('Invalid topic');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setContentFallback(null);
    getGuestTopic(guestId, id)
      .then((t) => {
        setTopic(t);
        if (!t.content && guestId) {
          getGuestTopicContent(guestId, id).then((c) => setContentFallback(c ?? null));
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load topic'))
      .finally(() => setLoading(false));
  }, [isReady, guestId, topicId]);

  if (sessionError) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{sessionError}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/guest')}>
          Back to Try as Guest
        </Button>
      </Container>
    );
  }

  if (loading || !topic) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <CircularProgress />}
        {!loading && !topic && !error && <Typography>Topic not found.</Typography>}
        <Button sx={{ mt: 2 }} onClick={() => navigate('/guest/search')}>
          Back to search
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/guest/search')} sx={{ mb: 2 }}>
        Back to search
      </Button>
      <Paper elevation={0} sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
          {topic.title}
        </Typography>
        {topic.courseName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            From course: {topic.courseName}
          </Typography>
        )}
        {(topic.estimatedTimeMinutes != null || topic.difficultyLevel) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {[topic.estimatedTimeMinutes != null && `${topic.estimatedTimeMinutes} min`, topic.difficultyLevel]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        )}

        {topic.description && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {topic.description}
            </Typography>
          </>
        )}

        {topic.learningObjectives && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Learning objectives
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {topic.learningObjectives}
            </Typography>
          </>
        )}

        {(topic.content || contentFallback) && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Content
            </Typography>
            <Box
              component="div"
              className="topic-content markdown-body"
              sx={{
                '& h1': { fontSize: '1.75rem', fontWeight: 600, mb: 2, mt: 3 },
                '& h2': { fontSize: '1.4rem', fontWeight: 600, mb: 1.5, mt: 2.5, borderBottom: '1px solid', borderColor: 'divider', pb: 1 },
                '& h3': { fontSize: '1.15rem', fontWeight: 600, mb: 1, mt: 2 },
                '& p': { mb: 1.5, lineHeight: 1.7 },
                '& ul, & ol': { pl: 3, mb: 2 },
                '& li': { mb: 0.5 },
                '& blockquote': { borderLeft: '4px solid', borderColor: 'primary.main', bgcolor: 'action.hover', m: 0, p: 2, mb: 2, borderRadius: 1 },
                '& pre': { bgcolor: 'grey.900', color: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', mb: 2 },
                '& code': { fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                {(topic.content || contentFallback) ?? ''}
              </ReactMarkdown>
            </Box>
          </>
        )}

        {/* Practice Quiz – same as student, submitted via guest API (max 2 attempts) */}
        {topicId && (
          <Box sx={{ mt: 4 }}>
            <GuestPracticeQuizCard topicId={Number(topicId)} />
          </Box>
        )}

        {/* Assessments – same as student, guest can try one (max 1 attempt) */}
        {topicId && (
          <Box sx={{ mt: 4 }}>
            <GuestAssessmentCard topicId={Number(topicId)} />
          </Box>
        )}

        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You're viewing this topic as a guest. Sign up to save progress and access the full course.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={() => navigate('/signup')}>
            Sign up to enrol
          </Button>
          {topic.courseId != null && (
            <Button variant="outlined" onClick={() => navigate(`/guest/course/${topic.courseId}`)}>
              View course as guest
            </Button>
          )}
          <Button variant="text" onClick={() => navigate('/guest/search')}>
            Back to search
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default GuestTopicView;
